import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TenantProvider } from './contexts/TenantContext';

// Public pages (eagerly loaded — critical for first paint)
import LandingPage from './pages/public/LandingPage';
import CareerPage from './pages/public/CareerPage';
import ApplicationForm from './pages/public/ApplicationForm';
import InterviewPage from './pages/public/InterviewPage';
import FlowInterviewPage from './pages/public/FlowInterviewPage';
import RegisterInvite from './pages/public/RegisterInvite';
import LegalAbout from './pages/public/LegalAbout';
import LegalPrivacy from './pages/public/LegalPrivacy';
import LegalKvkkAydinlatma from './pages/public/LegalKvkkAydinlatma';
import LegalDelivery from './pages/public/LegalDelivery';
import LegalDistanceSales from './pages/public/LegalDistanceSales';
import CaseStudySubmit from './pages/public/CaseStudySubmit';
import WithdrawApplicationPage from './pages/public/WithdrawApplicationPage';

// Auth pages (eagerly loaded — needed for login flow)
import Login from './pages/auth/Login';
import OnboardingWizard from './pages/auth/OnboardingWizard';
import OnboardingCheckout from './pages/auth/OnboardingCheckout';
import PasswordReset from './pages/auth/PasswordReset';
import PaymentPage from './pages/PaymentPage';

// Components
import CookieConsentModal from './components/CookieConsentModal';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

// Protected pages — lazy loaded for code splitting (reduces initial bundle)
const Dashboard = React.lazy(() => import('./pages/dashboard/Dashboard'));
const PositionManagement = React.lazy(() => import('./pages/positions/PositionManagement'));
const ApplicationManagement = React.lazy(() => import('./pages/applications/ApplicationManagement'));
const ApplicationDetail = React.lazy(() => import('./pages/applications/ApplicationDetail'));
const InterviewManagement = React.lazy(() => import('./pages/interviews/InterviewManagement'));
const InterviewDetail = React.lazy(() => import('./pages/interviews/InterviewDetail'));
const AntiCheatingDashboard = React.lazy(() => import('./pages/superhr/AntiCheatingDashboard'));
const TeamManagement = React.lazy(() => import('./pages/team/TeamManagement'));
const Settings = React.lazy(() => import('./pages/settings/Settings'));
const SuperAdminDashboard = React.lazy(() => import('./pages/admin/SuperAdminDashboard'));
const QuestionBank = React.lazy(() => import('./pages/content/QuestionBank'));
const QuestionSets = React.lazy(() => import('./pages/content/QuestionSets'));
const CaseStudyTemplates = React.lazy(() => import('./pages/content/CaseStudyTemplates'));
const CaseStudyBuilder = React.lazy(() => import('./pages/content/CaseStudyBuilder'));
const PlanUpgrade = React.lazy(() => import('./pages/billing/PlanUpgrade'));
const SubscriptionManagement = React.lazy(() => import('./pages/billing/SubscriptionManagement'));
const InterviewFlowList = React.lazy(() => import('./pages/interview-flows').then(m => ({ default: m.InterviewFlowList })));
const InterviewFlowBuilder = React.lazy(() => import('./pages/interview-flows').then(m => ({ default: m.InterviewFlowBuilder })));

// Loading fallback for lazy-loaded routes
const PageLoader: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <div className="inline-block w-8 h-8 border-4 border-gray-200 border-t-amber-500 rounded-full animate-spin" />
        <p className="mt-3 text-sm text-gray-500">{t('common.loading')}</p>
      </div>
    </div>
  );
};

const DefaultAppRoute: React.FC = () => {
  const { user, isLoading } = useAuth();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-lotus-gold mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">{t('common.loadingDashboard')}</p>
        </div>
      </div>
    );
  }

  if (user?.role === 'admin') {
    return <Navigate to="/app/admin" replace />;
  }
  return <Navigate to="/app/dashboard" replace />;
};

// Legacy klasik mülakat rotasını yeni flow tabanlı mülakata yönlendir
const InterviewRedirect: React.FC = () => {
  const { interviewId } = useParams<{ interviewId: string }>();
  const location = useLocation();

  const search = location.search || '';
  const targetId = interviewId || '';

  return <Navigate to={`/flow-interview/${targetId}${search}`} replace />;
};

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <TenantProvider>
            <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/careers/:tenantSlug" element={<CareerPage />} />
            <Route path="/careers/:tenantSlug/apply/:positionId" element={<ApplicationForm />} />
            {/* Eski klasik rota -> flow tabanlı mülakata yönlendirilir */}
            <Route path="/interview/:interviewId" element={<InterviewRedirect />} />
            <Route path="/flow-interview/:interviewId" element={<FlowInterviewPage />} />
            <Route path="/register-invite" element={<RegisterInvite />} />
            <Route path="/legal/about" element={<LegalAbout />} />
            <Route path="/legal/privacy" element={<LegalPrivacy />} />
            <Route path="/legal/kvkk-aydinlatma" element={<LegalKvkkAydinlatma />} />
            <Route path="/legal/delivery" element={<LegalDelivery />} />
            <Route path="/legal/distance-sales" element={<LegalDistanceSales />} />
            <Route path="/plan-upgrade" element={<PlanUpgrade />} />
            <Route path="/case-study/:token" element={<CaseStudySubmit />} />
            <Route path="/withdraw/:withdrawToken" element={<WithdrawApplicationPage />} />

            {/* Auth routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/onboarding" element={<OnboardingWizard />} />
            <Route path="/onboarding/checkout" element={<OnboardingCheckout />} />
            <Route path="/reset-password" element={<PasswordReset />} />
            <Route path="/payment" element={<PaymentPage />} />

            {/* Protected routes */}
            <Route path="/app" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<DefaultAppRoute />} />
              <Route path="dashboard" element={<Suspense fallback={<PageLoader />}><Dashboard /></Suspense>} />
              <Route path="positions" element={<Suspense fallback={<PageLoader />}><PositionManagement /></Suspense>} />
              <Route path="applications" element={<Suspense fallback={<PageLoader />}><ApplicationManagement /></Suspense>} />
              <Route path="applications/:applicationId" element={<Suspense fallback={<PageLoader />}><ApplicationDetail /></Suspense>} />
              <Route path="interviews" element={<Suspense fallback={<PageLoader />}><InterviewManagement /></Suspense>} />
              <Route path="interviews/:interviewId" element={<Suspense fallback={<PageLoader />}><InterviewDetail /></Suspense>} />
              <Route path="anti-cheating" element={<Suspense fallback={<PageLoader />}><AntiCheatingDashboard /></Suspense>} />
              <Route path="content/question-bank" element={<Suspense fallback={<PageLoader />}><QuestionBank /></Suspense>} />
              <Route path="content/question-sets" element={<Suspense fallback={<PageLoader />}><QuestionSets /></Suspense>} />
              <Route path="content/case-study-templates" element={<Suspense fallback={<PageLoader />}><CaseStudyTemplates /></Suspense>} />
              <Route path="content/case-study-templates/new" element={<Suspense fallback={<PageLoader />}><CaseStudyBuilder /></Suspense>} />
              <Route path="content/case-study-templates/:templateId" element={<Suspense fallback={<PageLoader />}><CaseStudyBuilder /></Suspense>} />
              <Route path="interview-flows" element={<Suspense fallback={<PageLoader />}><InterviewFlowList /></Suspense>} />
              <Route path="interview-flows/new" element={<Suspense fallback={<PageLoader />}><InterviewFlowBuilder /></Suspense>} />
              <Route path="interview-flows/:flowId" element={<Suspense fallback={<PageLoader />}><InterviewFlowBuilder /></Suspense>} />
              <Route path="team" element={<Suspense fallback={<PageLoader />}><TeamManagement /></Suspense>} />
              <Route path="settings" element={<Suspense fallback={<PageLoader />}><Settings /></Suspense>} />
              <Route path="billing" element={<Suspense fallback={<PageLoader />}><PlanUpgrade /></Suspense>} />
              <Route path="subscription" element={<Suspense fallback={<PageLoader />}><SubscriptionManagement /></Suspense>} />
              <Route path="admin" element={<Suspense fallback={<PageLoader />}><SuperAdminDashboard /></Suspense>} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        
        {/* Cookie Consent Modal - Global (inside TenantProvider for context) */}
        <CookieConsentModal
          onAccept={(preferences) => {
            console.log('Cookie preferences accepted:', preferences);
          }}
          onReject={() => {
            console.log('Cookie consent rejected');
          }}
        />
      </TenantProvider>
    </AuthProvider>
    </Router>
    </ErrorBoundary>
  );
}

export default App;
